/**
 * Admin "Missing Metadata" page — the consolidated backlog of titles
 * that user devices couldn't find on TMDB. Driven by the v1.2.12
 * server-side missing-metadata reporting flow (devices POST batches
 * to `/api/player/missing-metadata`).
 *
 * The admin can:
 *   • Browse the list, ranked by report-count (most-missed first).
 *   • Filter by kind (movie / series).
 *   • Search the open TMDB lookup page directly from each row.
 *   • Dismiss an entry once they've manually backfilled the metadata.
 */
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { adminAxios } from "../lib/api";
import { Database, RefreshCw, ExternalLink, Trash2, Search, Check } from "lucide-react";

function fmt(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return String(ts);
  }
}

export default function MissingMetadata() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState(""); // "" | "movie" | "series"
  const [q, setQ] = useState("");
  const [deleting, setDeleting] = useState(null);
  // Per-row state for the inline "Map to TMDB id" override flow.
  const [editingId, setEditingId] = useState(null);
  const [tmdbIdInput, setTmdbIdInput] = useState("");
  const [savingMap, setSavingMap] = useState(null);

  const startMap = (it) => {
    setEditingId(it.id);
    setTmdbIdInput("");
  };

  const submitMap = async (it) => {
    const tmdbId = parseInt(String(tmdbIdInput).trim(), 10);
    if (!tmdbId || tmdbId <= 0) {
      toast({ title: "TMDB id required", variant: "destructive" });
      return;
    }
    setSavingMap(it.id);
    try {
      await adminAxios.post("/api/admin/metadata/resolver/override", {
        // Server expects `tv` or `movie` — our missing-metadata table
        // uses `series` for TV.
        kind: it.kind === "series" ? "tv" : "movie",
        name: it.name,
        year: it.year || null,
        tmdb_id: tmdbId,
      });
      // Dismiss the row from the missing-metadata backlog — the mapping
      // is now in the resolver so every device that asks again will get
      // the right answer.
      await adminAxios.delete(`/api/admin/missing-metadata/${it.id}`);
      toast({
        title: "Mapped",
        description: `${it.name} → TMDB id ${tmdbId}. Devices will pick it up on next refresh.`,
      });
      setEditingId(null);
      setTmdbIdInput("");
      await load();
    } catch (err) {
      toast({
        title: "Couldn't save mapping",
        description: err?.response?.data?.detail || err.message,
        variant: "destructive",
      });
    } finally {
      setSavingMap(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (kindFilter) params.set("kind", kindFilter);
      params.set("limit", "500");
      const r = await adminAxios.get(
        `/api/admin/missing-metadata?${params.toString()}`
      );
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (err) {
      toast({
        title: "Couldn't load missing-metadata backlog",
        description: err?.response?.data?.detail || err?.message || "",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [kindFilter, load]);

  const onDelete = async (id) => {
    setDeleting(id);
    try {
      await adminAxios.delete(`/api/admin/missing-metadata/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      setTotal((n) => Math.max(0, n - 1));
    } catch (err) {
      toast({
        title: "Delete failed",
        description: err?.response?.data?.detail || err?.message || "",
        variant: "destructive",
      });
    } finally {
      setDeleting(null);
    }
  };

  const filtered = q.trim()
    ? items.filter((it) => (it.name || "").toLowerCase().includes(q.trim().toLowerCase()))
    : items;

  return (
    <div className="p-6 space-y-5" data-testid="admin-missing-metadata">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="w-6 h-6 text-amber-500" />
            Missing Metadata
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Titles user devices couldn&apos;t find on TMDB. {total.toLocaleString()} entries.
          </p>
        </div>
        <Button onClick={load} variant="outline" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <div className="flex gap-1.5">
            {[
              { v: "", l: "All" },
              { v: "movie", l: "Movies" },
              { v: "series", l: "Series" },
            ].map((opt) => (
              <button
                key={opt.v}
                data-testid={`missing-metadata-filter-${opt.v || "all"}`}
                onClick={() => setKindFilter(opt.v)}
                className={`px-3 py-1.5 rounded-md text-sm border ${
                  kindFilter === opt.v
                    ? "bg-amber-500 text-white border-amber-500"
                    : "bg-white border-slate-200 hover:bg-slate-50"
                }`}
              >
                {opt.l}
              </button>
            ))}
          </div>
          <div className="flex-1 min-w-[200px] relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Filter by title…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-8"
              data-testid="missing-metadata-search"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead className="w-20">Type</TableHead>
                <TableHead className="w-20">Year</TableHead>
                <TableHead className="w-24 text-right">Reports</TableHead>
                <TableHead className="w-24 text-right">Devices</TableHead>
                <TableHead className="w-44">Last reported</TableHead>
                <TableHead className="w-60 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-slate-400">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-slate-400">
                    No missing-metadata reports yet. Devices report items they
                    couldn&apos;t find via Settings → Report missing items.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                filtered.map((it) => {
                  const tmdbSearchUrl =
                    it.kind === "series"
                      ? `https://www.themoviedb.org/search/tv?query=${encodeURIComponent(it.name)}`
                      : `https://www.themoviedb.org/search/movie?query=${encodeURIComponent(it.name)}`;
                  return (
                    <TableRow key={it.id} data-testid={`missing-metadata-row-${it.id}`}>
                      <TableCell className="font-medium">{it.name}</TableCell>
                      <TableCell className="capitalize">{it.kind}</TableCell>
                      <TableCell>{it.year || "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">{it.report_count}</TableCell>
                      <TableCell className="text-right tabular-nums">{it.device_count}</TableCell>
                      <TableCell className="text-xs text-slate-500">{fmt(it.last_reported_at)}</TableCell>
                      <TableCell className="text-right space-x-1.5">
                        {editingId === it.id ? (
                          <span className="inline-flex items-center gap-1.5">
                            <Input
                              autoFocus
                              placeholder="TMDB id"
                              value={tmdbIdInput}
                              onChange={(e) => setTmdbIdInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") submitMap(it);
                                if (e.key === "Escape") setEditingId(null);
                              }}
                              className="h-7 w-24 text-xs"
                              inputMode="numeric"
                              data-testid={`missing-metadata-tmdb-input-${it.id}`}
                            />
                            <Button
                              variant="default"
                              size="sm"
                              disabled={savingMap === it.id}
                              onClick={() => submitMap(it)}
                              data-testid={`missing-metadata-save-${it.id}`}
                              className="h-7 px-2"
                            >
                              <Check className="w-3.5 h-3.5" />
                            </Button>
                          </span>
                        ) : (
                          <>
                            <a
                              href={tmdbSearchUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-sky-600 hover:text-sky-700 underline"
                            >
                              TMDB <ExternalLink className="w-3 h-3" />
                            </a>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => startMap(it)}
                              data-testid={`missing-metadata-map-${it.id}`}
                              className="h-7 px-2 text-xs"
                              title="Map to a TMDB id — instantly fixes this title across every device"
                            >
                              Map
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={deleting === it.id}
                              onClick={() => onDelete(it.id)}
                              data-testid={`missing-metadata-delete-${it.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
